function computeFinalPrice(params) {
  // Si params est un objet avec type et valeur, on extrait les paramètres
  let purchase, prep, fba, targetProfit, vatCollectedRate, vatDeductibleRate;
  
  if (params.valeur !== undefined) {
    // Format ancien
    purchase = params.coutTotal;
    prep = 0;
    fba = params.fba;
    targetProfit = Number(params.valeur);
    vatCollectedRate = params.tvaRate / 100;
    vatDeductibleRate = params.tvaDeductible ? params.tvaRate / 100 : 0;
  } else {
    // Format nouveau
    purchase = params.purchase;
    prep = params.prep || 0;
    fba = params.fba;
    targetProfit = params.targetProfit;
    vatCollectedRate = params.vatCollectedRate || 0.20;
    vatDeductibleRate = params.vatDeductibleRate || 0.20;
  }

  const referralRate = 0.15;
  const digitalRate = 0.03;
  // Frais digitaux séparés : 0.03 * FBA + 0.03 * Referral
  // On doit d'abord calculer le prix final pour calculer la part referral
  // On fait d'abord un calcul temporaire pour le prix final sans digital, puis on ajuste
  // Mais ici, on a déjà le prix final TTC calculé plus bas

  // TVA nette = TVA collectée - TVA déductible
  const vatCollected = (vatCollectedRate / (1 + vatCollectedRate));
  const vatDeductible = (vatDeductibleRate / (1 + vatDeductibleRate)) * purchase;

  const numerator = purchase + prep + fba + (digitalRate * fba) + targetProfit - vatDeductible;
  const totalRate = referralRate + referralRate * digitalRate + vatCollected;
  const finalPriceTTC = numerator / (1 - totalRate);

  // Frais Amazon détaillés
  // Referral brut (hors digital)
  const referralBrut = finalPriceTTC * referralRate;
  // Digital sur FBA
  const digitalOnFBA = digitalRate * fba;
  // Digital sur Referral
  const digitalOnReferral = digitalRate * referralBrut;
  // Total digital
  const digitalFees = digitalOnFBA + digitalOnReferral;
  // FBA hors digital
  const fbaNet = fba;
  // Referral hors digital
  const referralNet = referralBrut;
  // Total Amazon (hors digital)
  const totalAmazonFees = fbaNet + referralNet + digitalFees;

  // Calcul de la TVA pour le prix final
  const vatCollectedAmount = finalPriceTTC * vatCollected;
  const vatDeductibleAmount = purchase * (vatDeductibleRate / (1 + vatDeductibleRate));
  const vatNet = vatCollectedAmount - vatDeductibleAmount;

  // Calcul du profit
  const profit = finalPriceTTC - purchase - prep - totalAmazonFees - vatNet;
  
  return {
    price: parseFloat(finalPriceTTC.toFixed(2)),
    fees: {
      fba: parseFloat(fbaNet.toFixed(2)),
      referral: parseFloat(referralNet.toFixed(2)),
      digital: parseFloat(digitalFees.toFixed(2)),
      total: parseFloat(totalAmazonFees.toFixed(2))
    },
    vat: {
      collected: parseFloat(vatCollectedAmount.toFixed(2)),
      deductible: parseFloat(vatDeductibleAmount.toFixed(2)),
      net: parseFloat(vatNet.toFixed(2))
    },
    profit: parseFloat(profit.toFixed(2)),
    roi: parseFloat((profit / (purchase+prep) * 100).toFixed(2))
  };
}

function computeFinalPriceForMargin({
  purchase,
  prep = 0,
  fba,
  marginTarget,
  vatCollectedRate = 0.20,
  vatDeductibleRate = 0.20,
  referralRate = 0.15,
  digitalRate = 0.03
}) {
  const digitalOnFBA = digitalRate * fba;
  const vatDeductible = (vatDeductibleRate / (1 + vatDeductibleRate)) * purchase;
  
  // Partie variable dépendant du prix de vente TTC
  const variableRate =
    referralRate +
    (referralRate * digitalRate) +
    (vatCollectedRate / (1 + vatCollectedRate)) +
    marginTarget;

  const fixedCosts = purchase + prep + fba + digitalOnFBA - vatDeductible;

  const finalPriceTTC = fixedCosts / (1 - variableRate);

  // Calcul des frais Amazon pour le prix final
  const fbaFees = fba + digitalOnFBA;
  const referralFees = finalPriceTTC * referralRate * (1 + digitalRate);
  const totalAmazonFees = fbaFees + referralFees;

  // Calcul de la TVA pour le prix final
  const vatCollectedAmount = finalPriceTTC * (vatCollectedRate / (1 + vatCollectedRate));
  const vatDeductibleAmount = purchase * (vatDeductibleRate / (1 + vatDeductibleRate));
  const vatNet = vatCollectedAmount - vatDeductibleAmount;

  // Calcul du profit
  const profit = finalPriceTTC - purchase - prep - totalAmazonFees - vatNet;
  
  return {
    price: parseFloat(finalPriceTTC.toFixed(2)),
    fees: {
      fba: parseFloat(fbaFees.toFixed(2)),
      referral: parseFloat(referralFees.toFixed(2)),
      total: parseFloat(totalAmazonFees.toFixed(2))
    },
    vat: {
      collected: parseFloat(vatCollectedAmount.toFixed(2)),
      deductible: parseFloat(vatDeductibleAmount.toFixed(2)),
      net: parseFloat(vatNet.toFixed(2))
    },
    profit: parseFloat(profit.toFixed(2)),
    roi: parseFloat((profit / (purchase+prep) * 100).toFixed(2))
  };
}

/**
 * Calcule la TVA nette (collectée - déductible)
 * @param {number} prixVente - Prix TTC
 * @param {number} prixAchat - Prix d'achat TTC
 * @param {boolean} tvaDeductible - Si la TVA sur achat est déductible
 * @param {number} txTVA - Taux de TVA (ex: 20)
 * @param {string} pays - Code pays (FR, DE...)
 * @returns {object} { tvaCollectee, tvaDeductible, tvaNette, txTVACollecte, txTVADeductible }
 */
function getTVADetails({ prixVente, prixAchat, tvaDeductible, txTVA, pays }) {
  const TVA_BY_COUNTRY = {
    FR: 20, DE: 19, IT: 22, ES: 21, UK: 20, NL: 21, BE: 21, SE: 25, PL: 23, IE: 23
  };
  let txTVACollecte = TVA_BY_COUNTRY[pays] || 20;
  const tvaCollectee = prixVente && txTVACollecte ? prixVente * (txTVACollecte / 100) / (1 + txTVACollecte / 100) : 0;
  let tvaDeductibleVal = 0;
  let txTVADeductible = 0;
  if (tvaDeductible && txTVA) {
    txTVADeductible = txTVA;
    tvaDeductibleVal = prixAchat * (txTVADeductible / 100) / (1 + txTVADeductible / 100);
  }
  return {
    tvaCollectee,
    tvaDeductible: tvaDeductibleVal,
    tvaNette: tvaCollectee - tvaDeductibleVal,
    txTVACollecte,
    txTVADeductible
  };
}

/**
 * Calcule le profit fiscalement correct
 * @param {object} params
 * @returns {number}
 */
function getProfit({ prixVente, prixAchat, fraisAmazon, fraisPreparation = 0, fraisLivraison = 0, tvaDeductible, txTVA, pays }) {
  // TVA
  const details = getTVADetails({ prixVente, prixAchat, tvaDeductible, txTVA, pays });
  // Coût d'achat HT si TVA déductible
  const coutAchatHT = tvaDeductible ? (prixAchat - details.tvaDeductible) : prixAchat;
  // Profit = (Prix TTC - TVA collectée) - (Coût HT + frais) - frais Amazon + TVA déductible
  // Ou plus simplement :
  // Profit = Prix TTC - Coût HT - frais Amazon - frais préparation - frais livraison - TVA collectée
  return prixVente - coutAchatHT - fraisAmazon - fraisPreparation - fraisLivraison - details.tvaCollectee;
}

/**
 * Calcule la marge (%)
 * @param {number} profit
 * @param {number} prixVente
 * @returns {number}
 */
function getMargin({ profit, prixVente }) {
  if (!prixVente) return 0;
  return (profit / prixVente) * 100;
}

/**
 * Calcule le ROI (%)
 * @param {number} profit
 * @param {number} coutTotal
 * @returns {number}
 */
function getROI({ profit, coutTotal }) {
  if (!coutTotal) return 0;
  return (profit / coutTotal) * 100;
}

export default computeFinalPrice;
export { computeFinalPriceForMargin, getTVADetails, getProfit, getMargin, getROI }; 